import React, { useCallback, useEffect, useState } from "react";
import { Play, Eye, Save, Plus, Trash2, AlertTriangle } from "lucide-react";
import MaintenanceLayout from "../shared/MaintenanceLayout";
import { canEditMaintenance, canManageCampaigns, canApproveWaivers } from "../shared/MaintenanceGuard";
import {
  cashbackApi, CashbackProgram, CashbackTier, CashbackRow, CashbackSummary, ScanSummary, CashbackProgramUpdate,
} from "../../../api/maintenance/cashback";
import { campaignsApi, Campaign } from "../../../api/maintenance/campaigns";
import OverviewTab from "./OverviewTab";
import CampaignsTab from "./CampaignsTab";
import CampaignBuilder from "./CampaignBuilder";
import LaunchRequestsTab from "./LaunchRequestsTab";
import PerformanceTab from "./PerformanceTab";
import "../shared/maintenance-analytics.scss";
import { useNotice } from "../shared/useNotice";

type Tab = "overview" | "campaigns" | "build" | "overrides" | "performance" | "monitor" | "config";

const num = (v: number | null | undefined, d = 0) =>
  v == null ? "—" : v.toLocaleString("en-IN", { maximumFractionDigits: d, minimumFractionDigits: d });
const money = (v: number | null | undefined) => (v == null ? "—" : `₹${num(v, 0)}`);
const ts = (s: string | null) => (s ? new Date(s).toLocaleString("en-IN", { hour12: false }) : "—");

const statePill = (s: string): string => {
  if (s === "settled") return "ok";
  if (s === "rejected") return "bad";
  if (s.startsWith("pending")) return "warn";
  return "muted";
};

const emptyTier = (): CashbackTier => ({
  name: "", min_amount: 0, max_amount: null, is_random: false,
  min_percentage: null, max_percentage: null, fixed_percentage: null, priority: 100, is_active: true,
});

const CashbackConsole: React.FC = () => {
  const canEdit = canEditMaintenance();
  const canManage = canManageCampaigns();
  const canApprove = canApproveWaivers();
  const [tab, setTab] = useState<Tab>("overview");
  const [error, setError] = useNotice("error", "");
  const [msg, setMsg] = useNotice("success", "");

  // campaigns
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  // No campaignsLoading here any more: the Campaigns tab fetches its own
  // filtered page and owns its own spinner. Overview reads `campaigns` and
  // renders an empty state until it arrives.
  const [builderId, setBuilderId] = useState<number | null | undefined>(undefined); // undefined = builder closed
  const [pendingOverrides, setPendingOverrides] = useState(0);
  // Bumped whenever a campaign changes. The Campaigns tab fetches its own
  // filtered page, so it needs a signal rather than a new array.
  const [campaignsVersion, setCampaignsVersion] = useState(0);

  // Overview and Performance want every campaign; the Campaigns tab fetches its
  // own filtered page, since it owns the filters.
  const loadCampaigns = useCallback(async () => {
    try {
      const [c, lr] = await Promise.all([campaignsApi.list({ limit: 200 }), campaignsApi.launchRequests({ status: "pending", limit: 50 })]);
      setCampaigns(c.data.campaigns);
      setPendingOverrides(lr.data.length);
    } catch (e: any) {
      setError(e?.response?.data?.detail || "Failed to load campaigns (has migration 030 run?)");
    }
  }, []);

  const campaignsChanged = useCallback(() => { loadCampaigns(); setCampaignsVersion((v) => v + 1); }, [loadCampaigns]);

  const openCampaign = (c: Campaign) => { setBuilderId(c.id); setTab("build"); };
  const newCampaign = () => { setBuilderId(null); setTab("build"); };

  const [program, setProgram] = useState<CashbackProgram | null>(null);
  const [tiers, setTiers] = useState<CashbackTier[]>([]);
  const [summary, setSummary] = useState<CashbackSummary | null>(null);
  const [list, setList] = useState<CashbackRow[]>([]);
  const [scan, setScan] = useState<ScanSummary | null>(null);
  const [scanning, setScanning] = useState(false);
  const [editTier, setEditTier] = useState<CashbackTier | null>(null);

  const loadConfig = useCallback(async () => {
    try {
      const r = await cashbackApi.getConfig();
      setProgram(r.data.program);
      setTiers(r.data.tiers);
    } catch (e: any) {
      setError(e?.response?.data?.detail || "Failed to load config (has migration 023 run?)");
    }
  }, []);

  const loadMonitor = useCallback(async () => {
    try {
      const [s, l] = await Promise.all([cashbackApi.summary(), cashbackApi.list({ limit: 100 })]);
      setSummary(s.data);
      setList(l.data);
    } catch (e: any) {
      setError(e?.response?.data?.detail || "Failed to load cashbacks");
    }
  }, []);

  useEffect(() => { loadConfig(); loadMonitor(); loadCampaigns(); }, [loadConfig, loadMonitor, loadCampaigns]);

  const patchProgram = (p: CashbackProgramUpdate) => setProgram((cur) => (cur ? { ...cur, ...p } as CashbackProgram : cur));

  const saveProgram = async () => {
    if (!program) return;
    setError(""); setMsg("");
    try {
      await cashbackApi.updateConfig({
        is_active: program.is_active,
        min_payment_amount: program.min_payment_amount,
        lookback_days: program.lookback_days,
        max_cashback_cap: program.max_cashback_cap,
        per_user_monthly_cap: program.per_user_monthly_cap,
        approval_mode: program.approval_mode,
        audit_email: program.audit_email,
        min_lead_hours: program.min_lead_hours ?? undefined,
        announce_quiet_start: program.announce_quiet_start ?? undefined,
        announce_quiet_end: program.announce_quiet_end ?? undefined,
      });
      setMsg("Program saved.");
      loadConfig();
    } catch (e: any) {
      setError(e?.response?.data?.detail || "Save failed");
    }
  };

  const toggleActive = async () => {
    if (!program) return;
    const turningOn = !program.is_active;
    if (turningOn && !window.confirm(
      "Activate the cashback program?\n\nThe hourly scanner will start CREDITING REAL MONEY to user wallets for eligible recharges."
    )) return;
    patchProgram({ is_active: turningOn });
    try {
      await cashbackApi.updateConfig({ is_active: turningOn });
      setMsg(turningOn ? "Program ACTIVATED — crediting is now live." : "Program paused.");
    } catch (e: any) {
      patchProgram({ is_active: !turningOn });
      setError(e?.response?.data?.detail || "Toggle failed");
    }
  };

  const runScan = async (dryRun: boolean) => {
    if (!dryRun && !window.confirm(
      "Run a LIVE scan now?\n\nIf the program is active this credits eligible recharges immediately."
    )) return;
    setScanning(true); setError(""); setMsg("");
    try {
      const r = await cashbackApi.scan(dryRun);
      setScan(r.data);
      if (!dryRun) loadMonitor();
    } catch (e: any) {
      setError(e?.response?.data?.detail || "Scan failed");
    } finally {
      setScanning(false);
    }
  };

  const saveTier = async () => {
    if (!editTier) return;
    try {
      await cashbackApi.upsertTier(editTier);
      setEditTier(null);
      loadConfig();
    } catch (e: any) {
      setError(e?.response?.data?.detail || "Tier save failed");
    }
  };

  const removeTier = async (id?: number) => {
    if (!id || !window.confirm("Delete this tier?")) return;
    try { await cashbackApi.deleteTier(id); loadConfig(); }
    catch (e: any) { setError(e?.response?.data?.detail || "Delete failed"); }
  };

  const settleCb = async (c: CashbackRow) => {
    if (!window.confirm(`Settle cashback #${c.id}?\n\nCredits ₹${c.cashback_amount ?? 0} to ${c.user_name || "the user"}'s wallet.`)) return;
    setError(""); setMsg("");
    try { const r = await cashbackApi.approve(c.id); setMsg(`Cashback #${c.id}: ${r.data.transaction_state}${r.data.credited ? ` (₹${r.data.credited} credited)` : ""}.`); loadMonitor(); }
    catch (e: any) { setError(e?.response?.data?.detail || "Settle failed"); }
  };
  const rejectCb = async (c: CashbackRow) => {
    if (!window.confirm(`Reject cashback #${c.id}?`)) return;
    setError(""); setMsg("");
    try { await cashbackApi.reject(c.id); setMsg(`Cashback #${c.id} rejected.`); loadMonitor(); }
    catch (e: any) { setError(e?.response?.data?.detail || "Reject failed"); }
  };
  const isPending = (s: string) => s !== "settled" && s !== "rejected";

  const band = (t: CashbackTier) => `₹${num(t.min_amount)}–${t.max_amount == null ? "∞" : "₹" + num(t.max_amount)}`;
  const pct = (t: CashbackTier) => t.is_random ? `${t.min_percentage}–${t.max_percentage}% (rnd)` : `${t.fixed_percentage}% (fixed)`;

  return (
    <MaintenanceLayout title="Cashback Campaigns">
      <div className="ma">
        <div className="ma-tabs" style={{ flexWrap: "wrap" }}>
          <button className={`ma-tab ${tab === "overview" ? "on" : ""}`} onClick={() => setTab("overview")}>Overview</button>
          <button className={`ma-tab ${tab === "campaigns" ? "on" : ""}`} onClick={() => setTab("campaigns")}>Campaigns</button>
          <button className={`ma-tab ${tab === "build" ? "on" : ""}`} onClick={() => { if (builderId === undefined) setBuilderId(null); setTab("build"); }}>
            {builderId === undefined || builderId === null ? "Build a campaign" : "Campaign"}
          </button>
          <button className={`ma-tab ${tab === "overrides" ? "on" : ""}`} onClick={() => setTab("overrides")}>
            Override requests{pendingOverrides > 0 && <span className="ma-pill warn" style={{ marginLeft: 6 }}>{pendingOverrides}</span>}
          </button>
          <button className={`ma-tab ${tab === "performance" ? "on" : ""}`} onClick={() => setTab("performance")}>Performance</button>
          <button className={`ma-tab ${tab === "monitor" ? "on" : ""}`} onClick={() => setTab("monitor")}>Payouts</button>
          <button className={`ma-tab ${tab === "config" ? "on" : ""}`} onClick={() => setTab("config")}>Standing card &amp; settings</button>
        </div>

        {error && <div className="ma-error">{error}</div>}
        {msg && <div className="ma-note">{msg}</div>}

        {tab === "overview" && <OverviewTab campaigns={campaigns} onOpen={openCampaign} onNew={newCampaign} canManage={canManage} />}
        {tab === "campaigns" && <CampaignsTab onOpen={openCampaign} onNew={newCampaign} canManage={canManage} refreshKey={campaignsVersion} />}
        {tab === "build" && (
          <CampaignBuilder
            key={builderId === undefined ? "new" : String(builderId)}
            campaignId={builderId ?? null}
            minLeadHours={program?.min_lead_hours ?? 12}
            canManage={canManage}
            canApprove={canApprove}
            onChanged={campaignsChanged}
            onClose={() => { setBuilderId(undefined); setTab("campaigns"); }}
          />
        )}
        {tab === "overrides" && <LaunchRequestsTab canApprove={canApprove} onChanged={campaignsChanged} />}
        {tab === "performance" && <PerformanceTab campaigns={campaigns} />}

        {program && (tab === "monitor" || tab === "config") && (
          <div className="ma-note" style={{ display: "flex", alignItems: "center", gap: 12 }}>
            {program.is_active
              ? <span><span className="ma-pill ok">ACTIVE</span> hourly scanner credits eligible recharges automatically ({program.approval_mode} mode).</span>
              : <span><span className="ma-pill muted">PAUSED</span> nothing is credited — scans are preview-only until you activate.</span>}
            {canEdit && (
              <button className={`ma-btn ${program.is_active ? "" : "primary"}`} style={{ marginLeft: "auto" }} onClick={toggleActive}>
                {program.is_active ? "Pause program" : "Activate program"}
              </button>
            )}
          </div>
        )}

        {tab === "monitor" && (
          <>
            <div className="ma-kpis">
              <div className="ma-kpi">
                <div className="ma-kpi-label">Settled this month</div>
                <div className="ma-kpi-value" style={{ color: "#c62828" }}>−{money(summary?.settled_this_month_amount)}</div>
                <div className="ma-kpi-sub">{num(summary?.settled_this_month_count)} cashbacks</div>
              </div>
              {["pending_approval_1", "settled", "rejected"].map((st) => (
                <div className="ma-kpi" key={st}>
                  <div className="ma-kpi-label">{st === "pending_approval_1" ? "Pending" : st[0].toUpperCase() + st.slice(1)}</div>
                  <div className="ma-kpi-value">{num(summary?.by_state?.[st]?.count || 0)}</div>
                  <div className="ma-kpi-sub">{money(summary?.by_state?.[st]?.amount || 0)} total</div>
                </div>
              ))}
            </div>

            <div className="ma-card" style={{ marginBottom: 16 }}>
              <div className="ma-card-h">
                <h3>Run scanner</h3>
                <div style={{ display: "flex", gap: 8 }}>
                  <button className="ma-btn" disabled={scanning} onClick={() => runScan(true)}><Eye size={14} /> Preview (dry run)</button>
                  {canEdit && <button className="ma-btn primary" disabled={scanning} onClick={() => runScan(false)}><Play size={14} /> Run now (live)</button>}
                </div>
              </div>
              <div className="ma-card-b">
                {scanning && <p className="ma-loading">Scanning…</p>}
                {!scanning && !scan && <p className="ma-hint">Preview shows what the next hourly run would credit — inserts nothing. "Run now" credits immediately if the program is active.</p>}
                {!scanning && scan && (
                  <>
                    <div className="ma-legend" style={{ marginBottom: 10 }}>
                      <span className={`ma-pill ${scan.mode === "live" ? "ok" : "info"}`}>{scan.mode}</span>
                      <span className="ma-lg">Eligible <b>{scan.eligible_count}</b></span>
                      <span className="ma-lg">Created <b>{scan.created}</b></span>
                      <span className="ma-lg">Credited <b>{money(scan.credited_amount)}</b></span>
                      <span className="ma-lg">Previewed <b>{scan.previewed}</b></span>
                      <span className="ma-lg">No-wallet <b>{scan.skipped_no_wallet}</b></span>
                      <span className="ma-lg">No-tier <b>{scan.skipped_no_tier}</b></span>
                      <span className="ma-lg">Capped <b>{scan.skipped_capped}</b></span>
                      <span className="ma-lg">Dupe <b>{scan.skipped_exists}</b></span>
                    </div>
                    <div className="ma-tablewrap">
                      <table className="ma-table">
                        <thead><tr><th>Payment #</th><th>User</th><th className="num">Recharge</th><th>Tier</th><th className="num">%</th><th className="num">Cashback</th><th>Status</th></tr></thead>
                        <tbody>
                          {scan.rows.slice(0, 100).map((r) => (
                            <tr key={r.payment_order_id}>
                              <td>{r.payment_order_id}</td>
                              <td>{r.user_name || "—"}</td>
                              <td className="num">{money(r.payment_amount)}</td>
                              <td className="sub">{r.tier_name}</td>
                              <td className="num">{r.percentage}%</td>
                              <td className="num">{money(r.cashback_amount)}</td>
                              <td><span className={`ma-pill ${r.status === "settled" ? "ok" : r.status === "no_wallet" || r.status === "already_exists" ? "muted" : "info"}`}>{r.status}</span></td>
                            </tr>
                          ))}
                          {!scan.rows.length && <tr><td colSpan={7} className="ma-empty">No eligible recharges right now</td></tr>}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="ma-card">
              <div className="ma-card-h"><h3>Recent cashbacks</h3></div>
              <div className="ma-tablewrap">
                <table className="ma-table">
                  <thead><tr><th>User</th><th className="num">Recharge</th><th>Tier</th><th className="num">%</th><th className="num">Cashback</th><th>State</th><th>When</th>{canEdit && <th>Actions</th>}</tr></thead>
                  <tbody>
                    {list.map((c) => (
                      <tr key={c.id}>
                        <td>{c.user_name || "—"}<div className="sub">{c.organization || ""}</div></td>
                        <td className="num">{money(c.payment_amount)}</td>
                        <td className="sub">{c.tier_name || "—"}</td>
                        <td className="num">{c.percentage != null ? `${c.percentage}%` : "—"}</td>
                        <td className="num">{money(c.cashback_amount)}</td>
                        <td><span className={`ma-pill ${statePill(c.transaction_state)}`}>{c.transaction_state}</span></td>
                        <td className="sub">{ts(c.payment_created_at)}</td>
                        {canEdit && <td>{isPending(c.transaction_state) ? (
                          <div style={{ display: "flex", gap: 6 }}>
                            <button className="ma-btn primary" onClick={() => settleCb(c)}>Settle</button>
                            <button className="ma-btn" onClick={() => rejectCb(c)}>Reject</button>
                          </div>
                        ) : <span className="sub">—</span>}</td>}
                      </tr>
                    ))}
                    {!list.length && <tr><td colSpan={canEdit ? 8 : 7} className="ma-empty">No cashbacks yet</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {tab === "config" && program && (
          <>
            <div className="ma-card" style={{ marginBottom: 16 }}>
              <div className="ma-card-h"><h3>Program settings</h3>{canEdit && <button className="ma-btn primary" onClick={saveProgram}><Save size={14} /> Save</button>}</div>
              <div className="ma-card-b" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 14 }}>
                <label className="ma-select">Min recharge (₹)
                  <input type="number" value={program.min_payment_amount} disabled={!canEdit} onChange={(e) => patchProgram({ min_payment_amount: Number(e.target.value) })} /></label>
                <label className="ma-select">Lookback (days)
                  <input type="number" value={program.lookback_days} disabled={!canEdit} onChange={(e) => patchProgram({ lookback_days: Number(e.target.value) })} /></label>
                <label className="ma-select">Per-txn cap (₹) — blank = none
                  <input type="number" value={program.max_cashback_cap ?? ""} disabled={!canEdit} onChange={(e) => patchProgram({ max_cashback_cap: e.target.value === "" ? null : Number(e.target.value) })} /></label>
                <label className="ma-select">Per-user monthly cap (₹) — blank = none
                  <input type="number" value={program.per_user_monthly_cap ?? ""} disabled={!canEdit} onChange={(e) => patchProgram({ per_user_monthly_cap: e.target.value === "" ? null : Number(e.target.value) })} /></label>
                <label className="ma-select">Mode
                  <select value={program.approval_mode} disabled={!canEdit} onChange={(e) => patchProgram({ approval_mode: e.target.value })}>
                    <option value="auto">auto (credit immediately)</option>
                    <option value="manual">manual (create, credit on approval)</option>
                  </select></label>
                <label className="ma-select">Campaign lead time (hours)
                  <input type="number" value={program.min_lead_hours ?? 12} disabled={!canEdit} onChange={(e) => patchProgram({ min_lead_hours: Number(e.target.value) })} /></label>
                <label className="ma-select">Quiet hours start (IST, no push)
                  <input type="time" value={program.announce_quiet_start ?? "21:30"} disabled={!canEdit} onChange={(e) => patchProgram({ announce_quiet_start: e.target.value })} /></label>
                <label className="ma-select">Quiet hours end (IST)
                  <input type="time" value={program.announce_quiet_end ?? "08:00"} disabled={!canEdit} onChange={(e) => patchProgram({ announce_quiet_end: e.target.value })} /></label>
                <label className="ma-select" style={{ gridColumn: "1 / -1" }}>Hourly audit email(s) — comma-separated
                  <input type="text" value={program.audit_email ?? ""} disabled={!canEdit} placeholder="tech.fuel@chargingadda.com, ops@chargingadda.com"
                    onChange={(e) => patchProgram({ audit_email: e.target.value })} /></label>
              </div>
              <div className="ma-card-b" style={{ paddingTop: 0, color: "#8a92a1", fontSize: 12 }}>
                <AlertTriangle size={12} /> Caps are optional and off by default. Leaving them blank = no cap (matches current behaviour).
              </div>
            </div>

            <div className="ma-card">
              <div className="ma-card-h">
                <h3>Tiers <span className="ma-hint">· first matching band wins (by priority)</span></h3>
                {canEdit && <button className="ma-btn" onClick={() => setEditTier({ ...emptyTier(), program_id: program.id })}><Plus size={14} /> Add tier</button>}
              </div>

              {editTier && (
                <div className="ma-card-b" style={{ background: "#f7f8ff", borderBottom: "1px solid #e6e8ee", display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(140px,1fr))", gap: 10, alignItems: "end" }}>
                  <label className="ma-select">Name<input value={editTier.name} onChange={(e) => setEditTier({ ...editTier, name: e.target.value })} /></label>
                  <label className="ma-select">Min ₹<input type="number" value={editTier.min_amount} onChange={(e) => setEditTier({ ...editTier, min_amount: Number(e.target.value) })} /></label>
                  <label className="ma-select">Max ₹ (blank=∞)<input type="number" value={editTier.max_amount ?? ""} onChange={(e) => setEditTier({ ...editTier, max_amount: e.target.value === "" ? null : Number(e.target.value) })} /></label>
                  <label className="ma-select">Random?
                    <select value={editTier.is_random ? "1" : "0"} onChange={(e) => setEditTier({ ...editTier, is_random: e.target.value === "1" })}>
                      <option value="0">Fixed</option><option value="1">Random</option>
                    </select></label>
                  {editTier.is_random ? (
                    <>
                      <label className="ma-select">Min %<input type="number" value={editTier.min_percentage ?? ""} onChange={(e) => setEditTier({ ...editTier, min_percentage: e.target.value === "" ? null : Number(e.target.value) })} /></label>
                      <label className="ma-select">Max %<input type="number" value={editTier.max_percentage ?? ""} onChange={(e) => setEditTier({ ...editTier, max_percentage: e.target.value === "" ? null : Number(e.target.value) })} /></label>
                    </>
                  ) : (
                    <label className="ma-select">Fixed %<input type="number" value={editTier.fixed_percentage ?? ""} onChange={(e) => setEditTier({ ...editTier, fixed_percentage: e.target.value === "" ? null : Number(e.target.value) })} /></label>
                  )}
                  <label className="ma-select">Priority<input type="number" value={editTier.priority} onChange={(e) => setEditTier({ ...editTier, priority: Number(e.target.value) })} /></label>
                  <div style={{ display: "flex", gap: 6 }}>
                    <button className="ma-btn primary" onClick={saveTier}>Save</button>
                    <button className="ma-btn" onClick={() => setEditTier(null)}>Cancel</button>
                  </div>
                </div>
              )}

              <div className="ma-tablewrap">
                <table className="ma-table">
                  <thead><tr><th>Name</th><th>Band</th><th>Cashback</th><th className="num">Priority</th><th>Active</th>{canEdit && <th></th>}</tr></thead>
                  <tbody>
                    {tiers.map((t) => (
                      <tr key={t.id}>
                        <td>{t.name}</td>
                        <td className="sub">{band(t)}</td>
                        <td>{pct(t)}</td>
                        <td className="num">{t.priority}</td>
                        <td><span className={`ma-pill ${t.is_active ? "ok" : "muted"}`}>{t.is_active ? "yes" : "no"}</span></td>
                        {canEdit && <td><div style={{ display: "flex", gap: 6 }}>
                          <button className="ma-btn" onClick={() => setEditTier(t)}>Edit</button>
                          <button className="ma-btn" onClick={() => removeTier(t.id)}><Trash2 size={13} /></button>
                        </div></td>}
                      </tr>
                    ))}
                    {!tiers.length && <tr><td colSpan={canEdit ? 6 : 5} className="ma-empty">No tiers</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </MaintenanceLayout>
  );
};

export default CashbackConsole;
